import { useCallback, useEffect, useRef, useState } from "react";
import { cctvService } from "@/services";
import type { PtzPreset } from "@/services/types/cctv.types";
import type { PTZDirection } from "@/hooks/usePTZControl";

/**
 * 카메라 PTZ 세션 — 속도 수렴 루프와 프리셋.
 *
 * 큰 컨트롤러(`PTZControl`)와 작은 컨트롤러(`PTZControlCompact`)가 **같은 것을 쓴다**.
 * 복제하면 언마운트 정지 같은 안전 동작이 한쪽에서만 고쳐진다 — 카메라가 계속 도는
 * 종류의 버그라 한 벌로 둔다.
 */

/** 8방위 → 단위 벡터 (pan: 우+, tilt: 상+ — ONVIF continuous 속도 좌표계) */
const DIRECTION_VECTORS: Record<PTZDirection, { pan: number; tilt: number }> = {
  E: { pan: 1, tilt: 0 },
  NE: { pan: 0.707, tilt: 0.707 },
  N: { pan: 0, tilt: 1 },
  NW: { pan: -0.707, tilt: 0.707 },
  W: { pan: -1, tilt: 0 },
  SW: { pan: -0.707, tilt: -0.707 },
  S: { pan: 0, tilt: -1 },
  SE: { pan: 0.707, tilt: -0.707 },
};

/** 원하는 속도(desired)와 마지막 전송(sent)을 수렴시키는 루프 주기(ms) */
const SYNC_INTERVAL_MS = 200;
/** 같은 속도 유지 중 재전송 주기 — 연결 유실 등으로 명령이 증발해도 곧 복구되게 */
const KEEPALIVE_MS = 1000;

/** 속도 단계. 1~5 이며 최대 속도의 20~100% 로 쓴다 */
export const PTZ_SPEED_MIN = 1;
export const PTZ_SPEED_MAX = 5;
export const PTZ_SPEED_DEFAULT = 3;

/** continuous 속도 벡터 — 전부 0 이면 정지(stop) 의미 */
interface Velocity {
  pan: number;
  tilt: number;
  zoom: number;
}
const ZERO_VEL: Velocity = { pan: 0, tilt: 0, zoom: 0 };
const isZeroVel = (v: Velocity) => v.pan === 0 && v.tilt === 0 && v.zoom === 0;
const sameVel = (a: Velocity, b: Velocity) =>
  a.pan === b.pan && a.tilt === b.tilt && a.zoom === b.zoom;
const round3 = (n: number) => Math.round(n * 1000) / 1000;

/** 카메라 펌웨어(Hikvision ONVIF)가 ASCII 이름만 저장 — 한글 등은 □ 로 깨짐 (실측) */
export const isAsciiPresetName = (s: string) => /^[\x20-\x7E]*$/.test(s);

/**
 * Hikvision 펌웨어 내장 기능 프리셋(토큰 33~105 대역) — goto 시 위치 이동이 아니라
 * 특수 기능 실행(Remote reboot 포함)이고 삭제도 불가(204 반환하나 잔존) — 노출 금지
 */
const HIK_SYSTEM_PRESET =
  /^(Auto-flip|Back to origin|Call patrol \d+|Call pattern \d+|Day mode|Night mode|One-touch patrol|Day\/Night Auto Mode|Set manual limits|Save manual limits|Remote reboot|Call OSD menu|Stop a scan|Start (random|frame|auto|tilt|panorama) scan)$/;

/**
 * 사용자 프리셋만 추림.
 * 디폴트 슬롯명("Preset {token}") = 미설정 슬롯이라 함께 뺀다.
 */
function customPresets(list: PtzPreset[]): PtzPreset[] {
  return list.filter((p) => p.name !== `Preset ${p.token}` && !HIK_SYSTEM_PRESET.test(p.name));
}

export interface PTZSession {
  speed: number;
  setSpeed: (n: number) => void;
  /** 제어 실패 메시지. app 에 전역 toast 가 없어 각 컨트롤러가 인라인으로 낸다 */
  error: string | null;
  /** 조이스틱 입력 → 원하는 pan/tilt 속도 */
  setDesiredPanTilt: (direction: PTZDirection | null, distance: number) => void;
  /** 줌 버튼 홀드 — 누르는 동안 해당 속도, 떼면 0 */
  setDesiredZoom: (dir: 0 | 1 | -1) => void;
  presets: PtzPreset[];
  presetsLoading: boolean;
  presetBusy: boolean;
  gotoPreset: (token: string) => Promise<void>;
  savePreset: (name: string) => Promise<void>;
  deletePreset: (token: string) => Promise<void>;
}

/**
 * @param cctvId 제어 대상 — POST /cctvs/{id}/ptz
 * @param active 패널이 열려 있는가. 닫히면 수렴 루프를 멈추고 카메라를 세운다.
 */
export function usePTZSession(cctvId: number, active: boolean): PTZSession {
  const [speed, setSpeed] = useState(PTZ_SPEED_DEFAULT);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<PtzPreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetBusy, setPresetBusy] = useState(false);

  // 원하는 속도 — 입력(조이스틱/줌)이 갱신하고, 수렴 루프가 카메라에 반영 (리렌더 없음)
  const desiredRef = useRef<Velocity>(ZERO_VEL);
  // 마지막으로 카메라에 성공 전송한 속도와 시각
  const sentRef = useRef<{ v: Velocity; at: number }>({ v: ZERO_VEL, at: 0 });
  const speedRef = useRef(speed);
  const inFlightRef = useRef(false);

  // 렌더 중 ref 할당 대신 effect 미러링 (usePTZControl 의 optionsRef 와 동일 패턴)
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const setDesiredPanTilt = useCallback((direction: PTZDirection | null, distance: number) => {
    const cur = desiredRef.current;
    if (!direction || distance <= 0) {
      desiredRef.current = { ...cur, pan: 0, tilt: 0 };
      return;
    }
    const vec = DIRECTION_VECTORS[direction];
    const k = distance * (speedRef.current / PTZ_SPEED_MAX);
    desiredRef.current = { ...cur, pan: round3(vec.pan * k), tilt: round3(vec.tilt * k) };
  }, []);

  const setDesiredZoom = useCallback((dir: 0 | 1 | -1) => {
    desiredRef.current = {
      ...desiredRef.current,
      zoom: dir === 0 ? 0 : round3(dir * (speedRef.current / PTZ_SPEED_MAX)),
    };
  }, []);

  // 수렴 루프 — desired 가 바뀌면 continuous/stop 전송, 유지 중엔 keepalive 재전송.
  // stop 이 한 번 실패해도 desired=0 이 유지되는 한 다음 틱에 재시도된다.
  useEffect(() => {
    if (!active) return;
    desiredRef.current = ZERO_VEL;
    sentRef.current = { v: ZERO_VEL, at: 0 };

    const tick = async () => {
      if (inFlightRef.current) return;
      const desired = { ...desiredRef.current };
      const { v: sent, at } = sentRef.current;
      const changed = !sameVel(desired, sent);
      const needKeepalive = !isZeroVel(desired) && Date.now() - at >= KEEPALIVE_MS;
      if (!changed && !needKeepalive) return;

      inFlightRef.current = true;
      try {
        if (isZeroVel(desired)) await cctvService.ptzStop(cctvId);
        else await cctvService.ptzMove(cctvId, desired);
        sentRef.current = { v: desired, at: Date.now() };
        setError(null);
      } catch {
        setError("제어 명령이 실패했습니다. PTZ 접속 정보를 확인해주세요.");
      } finally {
        inFlightRef.current = false;
      }
    };
    const timer = setInterval(tick, SYNC_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      // 패널 닫힘/카메라 전환/언마운트: 카메라가 돌고 있으면 best-effort 정지
      if (!isZeroVel(sentRef.current.v)) cctvService.ptzStop(cctvId).catch(() => {});
      desiredRef.current = ZERO_VEL;
      sentRef.current = { v: ZERO_VEL, at: 0 };
    };
  }, [active, cctvId]);

  const loadPresets = useCallback(async () => {
    setPresetsLoading(true);
    try {
      setPresets(customPresets(await cctvService.ptzPresets(cctvId)));
      setError(null);
    } catch {
      setError("프리셋 목록을 불러오지 못했습니다.");
    } finally {
      setPresetsLoading(false);
    }
  }, [cctvId]);

  // 패널 열릴 때 프리셋 로드
  useEffect(() => {
    if (active) void loadPresets();
  }, [active, loadPresets]);

  const gotoPreset = useCallback(
    async (token: string) => {
      if (presetBusy) return;
      setPresetBusy(true);
      try {
        await cctvService.ptzPresetGoto(cctvId, token);
        setError(null);
      } catch {
        setError("프리셋 이동에 실패했습니다.");
      } finally {
        setPresetBusy(false);
      }
    },
    [cctvId, presetBusy]
  );

  const savePreset = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed || !isAsciiPresetName(trimmed) || presetBusy) return;
      setPresetBusy(true);
      try {
        await cctvService.ptzPresetSave(cctvId, trimmed);
        await loadPresets();
      } catch {
        setError("프리셋 저장에 실패했습니다.");
      } finally {
        setPresetBusy(false);
      }
    },
    [cctvId, presetBusy, loadPresets]
  );

  const deletePreset = useCallback(
    async (token: string) => {
      if (presetBusy) return;
      setPresetBusy(true);
      try {
        await cctvService.ptzPresetDelete(cctvId, token);
        await loadPresets();
      } catch {
        setError("프리셋 삭제에 실패했습니다.");
      } finally {
        setPresetBusy(false);
      }
    },
    [cctvId, presetBusy, loadPresets]
  );

  return {
    speed,
    setSpeed,
    error,
    setDesiredPanTilt,
    setDesiredZoom,
    presets,
    presetsLoading,
    presetBusy,
    gotoPreset,
    savePreset,
    deletePreset,
  };
}
